import { createBrowserRouter, Navigate } from 'react-router';

import { PublicRoute, PrivateRoute } from '@app/guards';
import { PublicLayout, MainLayout, UserContextLayout, CompanyContextLayout } from '@app/layout';

import { NotFoundPage } from '@pages/notFound';

import { Translation } from '@shared/config/i18n/i18nTranslations';
import { PATHS } from '@shared/constants';
import { FullscreenLoader } from '@shared/ui/loader';

import { pub, callbacks, user, company } from './routerConfig';

export const router = createBrowserRouter(
	[
		{
			HydrateFallback: FullscreenLoader,
			children: [
				{
					element: <PublicRoute />,
					errorElement: <NotFoundPage />,
					handle: {
						requiresAuth: false,
					},
					children: [
						{
							element: <PublicLayout />,
							children: [
								{
									index: true,
									lazy: pub.home,
									handle: {
										title: Translation.PAGE_TITLE_HOME,
									},
								},
								{
									path: PATHS.AUTH,
									lazy: pub.authFlow,
									handle: {
										title: Translation.PAGE_TITLE_SIGN_IN,
									},
								},
								{
									path: PATHS.callbacks.AUTH,
									lazy: callbacks.auth,
								},
							],
						},
					],
				},

				{
					element: <PrivateRoute />,
					errorElement: <Navigate to='/' replace />,
					handle: {
						requiresAuth: true,
					},
					children: [
						{
							element: <MainLayout />,
							children: [
								{
									path: ':accountMembershipId',
									children: [
										{
											element: <UserContextLayout />,
											children: [
												{
													path: PATHS.user.PROFILE,
													lazy: user.profileUser,
													handle: {
														title: Translation.USER_NAV_PROFILE,
													},
												},
												{
													path: PATHS.user.YOURDEBITCARD,
													lazy: user.debitCardUser,
													handle: {
														title: Translation.USER_NAV_YOUR_DEBIT_CARD,
													},
												},
												{
													path: PATHS.user.TRANSACTIONS,
													lazy: user.transactionsUser,
													handle: {
														title: Translation.USER_NAV_YOUR_TRANSACTIONS,
													},
												},
											],
										},

										{
											path: ':companyId',
											element: <CompanyContextLayout />,
											children: [
												{
													path: PATHS.company.COMPANY_PROFILE,
													lazy: company.companyProfile,
													handle: {
														title: Translation.COMPANY_NAV_COMPANY_PROFILE,
													},
												},
												{
													path: PATHS.company.EMPLOYEES,
													lazy: company.employees,
													handle: {
														title: Translation.COMPANY_NAV_EMPLOYEES,
													},
												},
												{
													path: PATHS.company.DEBITCARDS,
													children: [
														{
															index: true,
															lazy: company.debitCards,
															handle: {
																title: Translation.COMPANY_NAV_DEBIT_CARDS,
															},
														},
													],
												},
												{
													path: PATHS.company.BENEFITS,
													lazy: company.benefits,
													handle: {
														title: Translation.COMPANY_NAV_BENEFITS,
														sidebarMini: true,
													},
												},
												{
													path: PATHS.company.TOPUPPORDERS,
													lazy: company.topupOrders,
													handle: {
														title: Translation.COMPANY_NAV_TOPUP_ORDERS,
													},
												},
												{
													path: PATHS.company.TOPUPS,
													lazy: company.topups,
													handle: {
														title: Translation.COMPANY_NAV_TOPUPS,
													},
												},
											],
										},
									],
								},
							],
						},
					],
				},
				{
					// Guard-less on purpose: the consent popup lands here while the cardholder is
					// authenticated, so it must sit outside PublicRoute (which would redirect an
					// authed user back into the full app) and PrivateRoute. It only relays a signal
					// to the opener and closes its own tab.
					path: PATHS.callbacks.CONSENT,
					lazy: callbacks.consent,
				},
				{
					path: '*',
					element: <Navigate to='/' replace />,
				},
			],
		},
	],
	{
		scrollRestoration: 'manual',
	},
);
